/**
 * Quotation Service
 *
 * Handles quotation operations including CRUD, item management,
 * calculations, and approval workflow.
 *
 * @module services/quotationService
 */

import { supabase } from './supabase';
import { terminoParaFiltroSinNormalizar, normalizarTexto, columnaNormalizada } from '../utils/normalizacionBusqueda';
import {
  calculateQuotationTotals,
  validateQuotationData,
  validateQuotationReadyToSend,
  // #716 — el mismo predicado que usan los otros tres carriles.
  margenSinDefinir,
} from '../utils/calculations';
import { QUOTATION_STATUS, DEFAULT_TAX_RATE } from '../utils/constants';
// #717 — orden canonico de las lineas, y la posicion de una linea nueva.
import { ordenarLineas, siguienteSortOrder } from './ordenDeLineas';
// #609 — procedencia del precio unitario. Gemelo de
// `mcp-server/src/utils/priceProvenance.js` (test de paridad:
// `mcp-server/tests/tools/priceProvenanceLaneParity.test.js`).
import { PRICE_ROW_SOURCE, unconfirmedPricesMessage } from '../utils/priceProvenance';
import { createNotification } from './notificationService';
// #555 — una sola implementación de "avisar a los asignados" para todo el
// carril web. Ver el docblock de `notifyAssignees` en taskService.js.
import { notifyAssignees } from './taskService';
// #691 — la regla de datos de prueba de este arbol, escrita una sola vez.
import { fetchTestProjectIds, excluirPorProyectoDePrueba, avisoDatosDePrueba, contarOcultas } from './testDataScope';

/**
 * List quotations with optional filters
 * @param {Object} filters - Filter options
 * @returns {Promise<{data, count, error}>}
 */
export const listQuotations = async (filters = {}) => {
  try {
    // #691 (paridad de carriles) — este listado tampoco tenía la regla de #360.
    // `quotations` no lleva `is_test_data`: la marca vive en el proyecto, así
    // que se leen los ids una sola vez. En prod hoy es un no-op (ningún
    // proyecto está marcado), igual que cuando #360 tocó `listProjects`;
    // empieza a valer en cuanto se marque algo — que es exactamente lo que ya
    // pasa en dev, donde 6 de 9 cotizaciones eran fixtures de QA.
    const { ids: idsDePrueba, disponible } = await fetchTestProjectIds(supabase);

    let query = supabase
      .from('quotations')
      // #505: lista EXPLICITA, no `*`. `quotations` tiene risk_percentage y
      // profit_percentage -- el margen de ProMedic sobre la cotizacion. Esta
      // funcion la llama ClientDashboard.js:88, y /cotizaciones esta en
      // navConfig con rol CLIENT, asi que con `*` esos dos numeros viajaban al
      // browser del cliente. No se veian en pantalla; se leian en el JSON.
      //
      // Ningun lector de risk/profit pasa por aca: el unico es
      // QuotationBuilder.js:223, que carga por getQuotation() y ademas redirige
      // a cualquiera que no sea admin/tecnico. El resto de las apariciones en
      // src/ son ESCRITURAS.
      //
      // El gemelo MCP es parityTools.js (listQuotations/getQuotation), que lo
      // resuelve por rol con scrubQuotation(). Aca no hay rol a mano en el
      // cliente, asi que la columna simplemente no se pide -- que es la defensa
      // mas fuerte de las dos.
      .select(`
        id, project_id, quotation_number, status,
        subtotal, tax_rate, tax_amount, total,
        notes, created_by, approved_by, approved_at, sent_at,
        created_at, updated_at, supervisor_id, lider_id, sent_by, accepted_at,
        project:projects!quotations_project_id_fkey(id, project_number, title, client_id),
        created_by_profile:profiles!created_by(id, full_name),
        approved_by_profile:profiles!approved_by(id, full_name),
        quotation_items(id, description, quantity, unit_price, total_price)
      `, { count: 'exact' });

    // #717 — el orden de las líneas es explícito. Va acá, sobre el builder
    // recién creado, porque `.order()` devuelve el mismo builder y los filtros
    // condicionales de abajo lo siguen encadenando. Ver `services/ordenDeLineas.js`.
    query = ordenarLineas(query);

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    // #A4: el buscador y los filtros "Desde"/"Hasta" de /cotizaciones
    // alimentaban `filters` y morían acá — esta función sólo leía
    // projectId/status/page/pageSize. El usuario tecleaba un número de
    // cotización, el badge de filtros subía, y la lista era exactamente la
    // misma. Un filtro que no filtra es peor que ninguno: le hace creer al
    // usuario que lo que ve YA está acotado.
    //
    // La búsqueda es sobre `quotation_number` y nada más. Es lo único que
    // PostgREST puede filtrar en una sola pasada sin convertir el embed de
    // `projects` en un INNER JOIN (que además cambiaría lo que la RLS deja
    // ver). El placeholder de la UI dice exactamente eso, para no volver a
    // prometer de más.
    if (filters.search) {
      // #739 — `quotation_number` es un código y no necesita normalización de
      // tildes, pero SÍ el saneado: la inyección no depende de que la columna sea
      // texto humano, depende de que el término entre crudo en el filtro.
      query = query.ilike('quotation_number', `%${terminoParaFiltroSinNormalizar(filters.search)}%`);
    }

    // Rango por fecha de creación. `dateTo` es un `<input type="date">`, o
    // sea medianoche: sin extender al fin del día, elegir "hasta hoy" dejaba
    // afuera todo lo creado hoy.
    if (filters.dateFrom) {
      query = query.gte('created_at', new Date(`${filters.dateFrom}T00:00:00`).toISOString());
    }
    if (filters.dateTo) {
      query = query.lte('created_at', new Date(`${filters.dateTo}T23:59:59.999`).toISOString());
    }

    if (!filters.includeTestData) {
      query = excluirPorProyectoDePrueba(query, idsDePrueba);
    }

    const page = filters.page || 0;
    const pageSize = filters.pageSize || 20;
    query = query
      .range(page * pageSize, (page + 1) * pageSize - 1)
      .order('created_at', { ascending: false });

    const { data, count, error } = await query;
    if (error) throw error;

    // Filtrar en silencio es la mitad del defecto de #686: el aviso viaja
    // estructurado aunque hoy ninguna pantalla lo pinte todavía.
    let ocultas = 0;
    if (!filters.includeTestData && idsDePrueba.length > 0) {
      ocultas = await contarOcultas(() => {
        let c = supabase.from('quotations').select('id', { count: 'exact', head: true })
          .in('project_id', idsDePrueba);
        if (filters.projectId) c = c.eq('project_id', filters.projectId);
        if (filters.status) c = c.eq('status', filters.status);
        // EL MISMO filtro que la página, o el contador mide otro universo.
      if (filters.search) c = c.ilike('quotation_number', `%${terminoParaFiltroSinNormalizar(filters.search)}%`);
        return c;
      });
    }

    const testDataFilter = avisoDatosDePrueba({
      include: Boolean(filters.includeTestData), ocultas, sujeto: 'cotizaciones', disponible,
    });

    return { data, count, testDataFilter, error: null };
  } catch (error) {
    console.error('List quotations error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * List quotations assigned to the current user.
 *
 * Issue #131: dedicated "Mis Cotizaciones" inbox for the Líder role.
 * RLS already restricts what the user can see; this filter is purely
 * a UX scope reduction so the list shows only the user's own work.
 *
 * @param {Object} [options]
 * @param {boolean} [options.asCreator=false] - When true, filter by `created_by`
 *   instead of `lider_id`. Used so an admin viewing the inbox sees the
 *   quotations they created, with a banner explaining the difference.
 * @returns {Promise<{data, count, error}>}
 */
export const listMyQuotations = async ({ asCreator = false } = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) {
      return { data: [], count: 0, error: null };
    }

    const filterColumn = asCreator ? 'created_by' : 'lider_id';

    // #717 — dos órdenes distintos sobre la misma consulta: el `created_at`
    // descendente de abajo ordena las COTIZACIONES (la más nueva primero) y
    // `ordenarLineas` ordena el embed. Ver `services/ordenDeLineas.js`.
    const { data, count, error } = await ordenarLineas(
      supabase
        .from('quotations')
        .select(`
          *,
          project:projects!quotations_project_id_fkey(id, project_number, title, client_id),
          created_by_profile:profiles!created_by(id, full_name),
          supervisor:profiles!supervisor_id(id, full_name, role),
          lider:profiles!lider_id(id, full_name, role),
          quotation_items(id, description, quantity, unit_price, total_price)
        `, { count: 'exact' })
        .eq(filterColumn, user.id)
        .order('created_at', { ascending: false })
    );

    if (error) throw error;

    return { data: data || [], count: count || 0, error: null };
  } catch (error) {
    console.error('List my quotations error:', error);
    return { data: [], count: 0, error };
  }
};

/**
 * Get quotation by ID
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<{data, error}>}
 */
export const getQuotation = async (quotationId) => {
  try {
    // #717 — orden explícito de las líneas. Ver `services/ordenDeLineas.js`.
    const { data, error } = await ordenarLineas(
      supabase
        .from('quotations')
        .select(`
          *,
          project:projects!quotations_project_id_fkey(id, project_number, title, client_id, client:clients!projects_client_id_fkey(company_name, primary_contact_name, primary_contact_email)),
          created_by_profile:profiles!created_by(id, full_name),
          approved_by_profile:profiles!approved_by(id, full_name),
          supervisor:profiles!supervisor_id(id, full_name, role),
          lider:profiles!lider_id(id, full_name, role),
          quotation_items(*)
        `)
        .eq('id', quotationId)
    ).single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get quotation error:', error);
    return { data: null, error };
  }
};

/**
 * Create quotation
 * @param {Object} quotationData - Quotation data
 * @returns {Promise<{data, error}>}
 */
export const createQuotation = async (quotationData) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Validate quotation data
    const validation = validateQuotationData(quotationData);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    // Calculate a provisional total for the initial INSERT (issue #325:
    // this JS calc is NOT the source of truth — the quotation row doesn't
    // exist yet so the DB function can't be called for it. Once the items
    // below are inserted, the `recalculate_quotation_on_item_change`
    // trigger calls the canonical `calculate_quotation_totals()` DB
    // function and overwrites subtotal/tax_amount/total with the
    // authoritative value, so this is only a placeholder for the brief
    // window before that insert).
    // #457: tax_rate uses `??` — a caller-supplied 0 means "exempt, no IVA"
    // and must survive; only an absent/NULL rate falls back to 13%.
    //
    // M1: the parenthetical that used to close this comment said the `|| 0` on
    // risk/profit "is safe because there the fallback IS 0". That is exactly
    // the reasoning that produced 24 of 35 prod quotations at 0/0. The tax
    // rate has a company-wide default (13%); the margin has none — it varies
    // per client and per job, which is why the MCP lane declares both fields
    // REQUIRED and forbids the model from inferring them (#339). There is no
    // safe fallback to have.
    //
    // #700 — LA ÚLTIMA FRASE DE ESTE COMENTARIO QUEDÓ STALE Y HABÍA QUE
    // CORREGIRLA. Decía "`validateQuotationData` above now rejects the absence,
    // so anything reaching this line was actually chosen by someone". Eso valió
    // sólo entre M1 y #593: hoy `validateQuotationData` NO rechaza la ausencia
    // —la reporta como *warning*, porque guardar un borrador sin margen es el
    // estado normal del paso 1 del RACI— así que a esta línea SÍ llegan
    // márgenes en `undefined`/`null`, a propósito y de forma legítima.
    //
    // Por eso `calculateQuotationTotals` ya no puede confiar en que le llegue
    // un número: acepta la ausencia, calcula el costo (paridad exacta con el
    // COALESCE de la función de base) y lo declara en `marginsDefined`. Estos
    // totales son PROVISIONALES igual —el trigger los pisa con el valor
    // autoritativo apenas entran los ítems— así que lo que importa acá es que
    // los porcentajes se escriban como NULL, unas líneas más abajo, y no como 0.
    const totals = calculateQuotationTotals(
      quotationData.items || [],
      quotationData.risk_percentage,
      quotationData.profit_percentage,
      quotationData.tax_rate ?? DEFAULT_TAX_RATE
    );

    // Create quotation (issue #104: supervisor_id / lider_id default to
    // the current user if the caller didn't provide explicit handoff).
    const { data: quotation, error: quotationError } = await supabase
      .from('quotations')
      .insert({
        project_id: quotationData.project_id,
        status: QUOTATION_STATUS.DRAFT,
        subtotal: totals.itemsSubtotal,
        // #457: this is the WRITE. With `||`, a quotation created at 0% IVA
        // was persisted as 13% — the data was corrupt on disk, not just
        // mis-rendered, so re-opening the record could not reveal the loss.
        tax_rate: quotationData.tax_rate ?? DEFAULT_TAX_RATE,
        tax_amount: totals.taxAmount,
        total: totals.total,
        // M1 + #593: ni `|| 0` ni la clave omitida. `?? null` escribe un NULL
        // EXPLÍCITO, que es lo único que sobrevive a una columna que todavía
        // diga `DEFAULT 0` (producción no tiene la migración hasta que alguien
        // la aplique). NULL = nadie lo definió; 0 = se eligió cotizar a costo.
        risk_percentage: quotationData.risk_percentage ?? null,
        profit_percentage: quotationData.profit_percentage ?? null,
        notes: quotationData.notes,
        created_by: user.id,
        supervisor_id: quotationData.supervisor_id || user.id,
        lider_id: quotationData.lider_id || user.id,
      })
      .select()
      .single();

    if (quotationError) throw quotationError;

    // Create quotation items if provided (total_price is a generated column)
    if (quotationData.items && quotationData.items.length > 0) {
      const itemsToInsert = quotationData.items.map((item, index) => ({
        quotation_id: quotation.id,
        item_type: item.item_type,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        sort_order: index,
        // #609 — mismo criterio que `addQuotationItem`: acá el precio lo tipeó
        // una persona en el formulario, así que es 'stated' por construcción.
        // Se escribe explícito y NO se deja en NULL, porque NULL significa
        // "fila anterior a #609" y este carril sí puede afirmar de dónde salió
        // el número. Si el llamador manda una procedencia, manda la suya.
        price_source: item.price_source || PRICE_ROW_SOURCE.STATED,
      }));

      const { error: itemsError } = await supabase
        .from('quotation_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;
    }

    return {
      data: quotation,
      error: null,
      // M1: cotizar a costo es válido si alguien lo eligió — pero queda dicho.
      ...(validation.warnings?.length ? { margin_warning: validation.warnings[0] } : {}),
    };
  } catch (error) {
    console.error('Create quotation error:', error);
    return { data: null, error };
  }
};

/**
 * Update quotation
 * @param {string} quotationId - Quotation ID
 * @param {Object} updates - Quotation updates
 * @returns {Promise<{data, error}>}
 */
/**
 * #782 — una cotización emitida (approved/sent/accepted/rejected) no se edita
 * por ninguna puerta. Los cambios de estado van por sus propias funciones
 * (submitQuotationForReview / approveQuotation / rejectQuotation /
 * markQuotationSent); esto cubre las ediciones de contenido.
 */
// Literales y no `QUOTATION_STATUS.*`: hay suites que mockean `utils/constants`
// parcialmente y esto se evalúa al cargar el módulo.
export const QUOTATION_ESTADOS_NO_EDITABLES = ['approved', 'sent', 'accepted', 'rejected'];

export const assertQuotationEditable = (quotation, accion) => {
  if (!quotation || !QUOTATION_ESTADOS_NO_EDITABLES.includes(quotation.status)) return;
  const nombre = quotation.quotation_number || quotation.id;
  throw new Error(
    `No se puede ${accion} la cotización ${nombre}: está en estado "${quotation.status}", o sea que ya es ` +
    'un documento emitido y cambiarlo le cambia el precio que el cliente vio. Sólo se editan las ' +
    'cotizaciones en borrador o en revisión. Si está equivocada, rechazala dejando el motivo y armá una nueva.'
  );
};

export const updateQuotation = async (quotationId, updates) => {
  try {
    // Extract items from updates (they go to a separate table)
    const { items, ...quotationUpdates } = updates;

    const { data: actual, error: readError } = await supabase
      .from('quotations')
      .select('id, quotation_number, status')
      .eq('id', quotationId)
      .single();
    if (readError) throw readError;
    assertQuotationEditable(actual, 'editar');

    // Recalculate totals if items or percentages changed
    if (items || quotationUpdates.risk_percentage !== undefined || quotationUpdates.profit_percentage !== undefined) {
      // Get current items if not provided
      let itemsForCalc = items;
      if (!itemsForCalc) {
        const { data: currentQuotation } = await getQuotation(quotationId);
        itemsForCalc = currentQuotation?.quotation_items || [];
      }

      // Issue #325: provisional value only — the quotation UPDATE below
      // runs before the item delete/insert triggers have seen the final
      // item set, and (when risk/profit/tax_rate change) fires
      // `recalculate_quotation_on_percentage_change` right after, which
      // calls the canonical `calculate_quotation_totals()` DB function
      // and overwrites these fields with the authoritative value.
      const totals = calculateQuotationTotals(
        itemsForCalc,
        quotationUpdates.risk_percentage,
        quotationUpdates.profit_percentage,
        quotationUpdates.tax_rate ?? DEFAULT_TAX_RATE  // #457: 0% is a real rate
      );

      quotationUpdates.subtotal = totals.itemsSubtotal;
      quotationUpdates.tax_amount = totals.taxAmount;
      quotationUpdates.total = totals.total;
    }

    // Update quotation_items if items provided
    if (items && items.length > 0) {
      // Delete existing items
      await supabase
        .from('quotation_items')
        .delete()
        .eq('quotation_id', quotationId);

      // Insert new items (total_price is a generated column, don't include it)
      //
      // #609 — ESTE es el DELETE + INSERT que borraba el sello.
      // `QuotationBuilder` en modo edición pasa por acá, y sin `price_source`
      // toda línea que el agente había dejado `unconfirmed` volvía a insertarse
      // en NULL — o sea que apretar "Guardar borrador" APAGABA la compuerta,
      // sin que nadie decidiera nada y sin dejar rastro. Se reenvía lo que
      // venga; el formulario decide (con el ack de #609) cuándo una línea
      // trabada pasa a 'stated'. Una línea nueva del formulario no trae
      // procedencia y cae a 'stated', que es la verdad: la tipeó una persona.
      const itemsToInsert = items.map((item, index) => ({
        quotation_id: quotationId,
        item_type: item.item_type,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        sort_order: index,
        price_source: item.price_source || PRICE_ROW_SOURCE.STATED,
      }));

      const { error: itemsError } = await supabase
        .from('quotation_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;
    }

    // Update quotation record
    const { data, error } = await supabase
      .from('quotations')
      .update(quotationUpdates)
      .eq('id', quotationId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update quotation error:', error);
    return { data: null, error };
  }
};

/**
 * Add item to quotation
 * @param {string} quotationId - Quotation ID
 * @param {Object} item - Item data
 * @returns {Promise<{data, error}>}
 */
export const addQuotationItem = async (quotationId, item) => {
  try {
    // total_price is a generated column, don't include it
    const { data, error } = await supabase
      .from('quotation_items')
      .insert({
        quotation_id: quotationId,
        item_type: item.item_type,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        // #717 — `?? `, no `||`: un 0 explicito se respeta. Cuando no viene,
        // se calcula la siguiente posicion. Los caminos de carga MASIVA de este
        // archivo ya asignaban `sort_order: index`; el que agrega UNA linea
        // dejaba el `DEFAULT 0`, asi que una cotizacion armada linea por linea
        // terminaba con todas empatadas.
        sort_order: item.sort_order ?? await siguienteSortOrder(supabase, quotationId),
        // #609 — acá el precio lo tipeó una persona en el formulario
        // (`QuotationBuilder`), así que es 'stated' POR CONSTRUCCIÓN: no hay
        // modelo en el medio que pueda inventarlo. Se escribe explícito y no se
        // deja en NULL, porque NULL significa "fila anterior a #609" y este
        // carril tiene que poder afirmar de dónde salió cada número.
        price_source: item.price_source || PRICE_ROW_SOURCE.STATED,
      })
      .select()
      .single();

    if (error) throw error;

    // Recalculate quotation totals
    await recalculateQuotationTotals(quotationId);

    return { data, error: null };
  } catch (error) {
    console.error('Add quotation item error:', error);
    return { data: null, error };
  }
};

/**
 * Update quotation item
 * @param {string} itemId - Item ID
 * @param {Object} updates - Item updates
 * @returns {Promise<{data, error}>}
 */
export const updateQuotationItem = async (itemId, updates) => {
  try {
    // total_price is a generated column, remove it from updates if present
    const { total_price, ...cleanUpdates } = updates;

    // #609 — corregir el precio desde el formulario web es un humano diciendo
    // el número. Ése es además el camino por el que un admin destraba una línea
    // que el agente dejó `unconfirmed`: abre la cotización, mira el monto y lo
    // confirma. Si esto no reescribiera la procedencia, la única salida sería
    // borrar la línea.
    if (cleanUpdates.unit_price !== undefined && cleanUpdates.price_source === undefined) {
      cleanUpdates.price_source = PRICE_ROW_SOURCE.STATED;
    }

    const { data, error } = await supabase
      .from('quotation_items')
      .update(cleanUpdates)
      .eq('id', itemId)
      .select()
      .single();

    // #654 — CERO FILAS AFECTADAS NO ES "NO EXISTE".
    //
    // Un UPDATE que no matchea ninguna policy de RLS no da error: PostgREST
    // devuelve 200 con array vacío, y el `.single()` lo reporta como PGRST116.
    // Ese código significa literalmente "cero filas", y acá tiene DOS causas
    // que no se parecen en nada: la línea no existe, o existe y no la podés
    // escribir. Dejarlo pasar crudo hacía que el formulario mostrara "Cannot
    // coerce the result to a single JSON object" — un mensaje que no le dice a
    // nadie qué hacer, sobre una escritura que no ocurrió.
    if (error?.code === 'PGRST116') {
      throw new Error(
        `No se pudo corregir la línea ${itemId}: la actualización no escribió ninguna fila. ` +
        'O la línea ya no existe, o no tenés permiso para editar esa cotización ' +
        '(sólo se editan las cotizaciones en borrador o en revisión, y sólo quien las creó, ' +
        'el líder asignado o un administrador). Volvé a abrir la cotización para ver cómo ' +
        'quedó de verdad antes de reintentar.'
      );
    }
    if (error) throw error;

    // Recalculate quotation totals
    await recalculateQuotationTotals(data.quotation_id);

    return { data, error: null };
  } catch (error) {
    console.error('Update quotation item error:', error);
    return { data: null, error };
  }
};

/**
 * Delete quotation item
 * @param {string} itemId - Item ID
 * @returns {Promise<{success, error}>}
 */
export const deleteQuotationItem = async (itemId) => {
  try {
    // Get quotation ID before deleting
    const { data: item } = await supabase
      .from('quotation_items')
      .select('quotation_id, quotation:quotations(quotation_number, status)')
      .eq('id', itemId)
      .single();

    // #782 — mismo chequeo que el carril MCP: el trigger de la base exime al
    // admin, así que sin esto un admin vacía una cotización aceptada desde la web.
    assertQuotationEditable(item?.quotation, 'borrar una línea de');

    const { error } = await supabase
      .from('quotation_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;

    // Recalculate quotation totals
    if (item) {
      await recalculateQuotationTotals(item.quotation_id);
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete quotation item error:', error);
    return { success: false, error };
  }
};

/**
 * Recalculate quotation totals based on items.
 *
 * Issue #325: this used to reimplement the Wave Calculator arithmetic in
 * JS and write `subtotal`/`tax_amount`/`total` directly — one of FOUR
 * divergent implementations writing the same column (split-brain,
 * last-writer-wins). It now delegates to the DB function
 * `calculate_quotation_totals(uuid)` via RPC, the single source of truth
 * (also the function backing the `quotation_items`/`quotations`
 * triggers), instead of recomputing the formula here.
 *
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<{data, error}>}
 */
export const recalculateQuotationTotals = async (quotationId) => {
  try {
    const { error: rpcError } = await supabase.rpc('calculate_quotation_totals', {
      p_quotation_id: quotationId,
    });

    if (rpcError) throw rpcError;

    // The RPC already persisted subtotal/tax_amount/total — re-select the
    // full row so callers get the same shape as before.
    const { data, error } = await supabase
      .from('quotations')
      .select('*')
      .eq('id', quotationId)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Recalculate quotation totals error:', error);
    return { data: null, error };
  }
};

/**
 * Submit quotation for review
 * Creates or updates a review task for administrators
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<{data, error}>}
 */
export const submitQuotationForReview = async (quotationId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // #654 — Sin líneas no hay nada que revisar.
    //
    // La regla ya vivía en el carril MCP (`quotationTools.submitQuotationForReview`
    // → `assertQuotationHasItems`) y NO en éste: acá se pasaba directo al
    // `update status = 'under_review'`. El formulario la chequea antes
    // (`validateForm` → `validateQuotationData`), pero eso es la pantalla, no
    // el contrato del servicio: cualquier otro llamador —o un cambio en el
    // builder— manda una cotización vacía a revisión sin que nada se queje.
    // Es el mismo patrón de #353: arreglar un carril y dejar el otro.
    //
    // Falla CERRADA: si la cuenta no se puede leer, no se envía. El costo de
    // equivocarse para este lado es un envío que hay que reintentar; para el
    // otro, una cotización vacía en revisión que se lee igual que una real.
    const { count: cantidadItems, error: itemsError } = await supabase
      .from('quotation_items')
      .select('id', { count: 'exact', head: true })
      .eq('quotation_id', quotationId);

    if (itemsError) throw itemsError;

    if (!cantidadItems) {
      throw new Error(
        'No se puede enviar a revisión una cotización sin líneas. Agregá al ' +
        'menos un ítem (material, mano de obra o herramienta) y volvé a ' +
        'enviarla. Si todavía no tenés los ítems, guardala como borrador.'
      );
    }

    // Get user profile for name
    const { data: userProfile } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user.id)
      .single();

    // Update quotation status
    const { data: quotation, error: updateError } = await supabase
      .from('quotations')
      .update({ status: QUOTATION_STATUS.UNDER_REVIEW })
      .eq('id', quotationId)
      .select(`
        *,
        project:projects!quotations_project_id_fkey(id, project_number, title)
      `)
      .single();

    if (updateError) throw updateError;

    // Get all admin users
    const { data: admins, error: adminsError } = await supabase
      .from('profiles')
      .select('id')
      .eq('role', 'admin');

    if (adminsError) throw adminsError;

    const adminIds = admins?.map(a => a.id) || [];
    const timestamp = new Date().toLocaleString('es-CR', { timeZone: 'America/Costa_Rica' });
    const userName = userProfile?.full_name || 'Usuario';

    // #716 — LA TAREA QUE LEE ADMINISTRACIÓN NO PUEDE LLAMARLE "TOTAL" AL COSTO.
    //
    // Esto rotulaba `**Total:** $131.871,00` sobre una cotización con los dos
    // porcentajes en NULL — el provisional que la base guarda como si el margen
    // fuera 0 (#593). Y el lector de ese texto es exactamente quien tiene que
    // DECIDIR el margen: la persona a la que el número le llega mal rotulado es
    // la única que puede corregirlo.
    //
    // Es el gemelo web de lo que el carril MCP arregla en la respuesta de la
    // tool (#716). Mismo criterio que #706: el rótulo tiene que ser verdadero en
    // el payload donde se emite, así que es CONDICIONAL — con el margen decidido
    // "Total" es correcto y queda como estaba.
    const sinMargen = margenSinDefinir(quotation);
    const montoFormateado = `$${quotation.total?.toLocaleString('es-CR', { minimumFractionDigits: 2 }) || '0.00'}`;
    const montoDeLaTarea = sinMargen
      ? `**Costo provisional:** ${montoFormateado}\n**Todavía no tiene precio:** el % de riesgo y el % de `
        + `utilidad están sin definir, así que ese monto es el costo y no el precio. Hay que fijar `
        + `los dos porcentajes antes de aprobar.`
      : `**Total:** ${montoFormateado}`;
    const avisoMargen = sinMargen
      ? 'La cotización quedó en revisión, pero todavía no tiene precio: el % de riesgo y el % de '
        + 'utilidad están sin definir, así que el monto que se ve es el costo. Administración tiene '
        + 'que fijarlos antes de aprobar.'
      : null;

    // Check if there's an existing review task for this quotation
    const { data: existingTasks } = await supabase
      .from('tasks')
      .select('id')
      .eq('project_id', quotation.project_id)
      // #742 — la búsqueda de la tarea de revisión va contra `title_norm`, con el
      // literal normalizado del mismo lado. No es un sitio de #739 (no hay texto
      // del usuario: el literal es nuestro y el número sale de la base), pero sí
      // era frágil: comparaba un literal ACENTUADO contra la columna cruda, así
      // que un título escrito sin la tilde no matcheaba y este código creaba una
      // tarea duplicada en vez de encontrar la que ya existía.
      .ilike(
        columnaNormalizada('title'),
        `%${normalizarTexto('Revisión de Cotización')}%${normalizarTexto(quotation.quotation_number)}%`
      )
      .neq('status', 'completed')
      .limit(1);

    if (existingTasks && existingTasks.length > 0) {
      // Update existing task with a comment
      const taskId = existingTasks[0].id;
      const commentContent = `📝 **Cotización actualizada**\n\nLa cotización fue modificada por **${userName}** el ${timestamp}.\n\nPor favor revisar los cambios antes de aprobar.`;

      await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          comment: commentContent,
          created_by: user.id,
        });

      // Update task to pending if it was in progress
      await supabase
        .from('tasks')
        .update({
          status: 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId);
    } else {
      // Create new review task
      const { data: newTask, error: taskError } = await supabase
        .from('tasks')
        .insert({
          project_id: quotation.project_id,
          title: `Revisión de Cotización ${quotation.quotation_number}`,
          description: `La cotización **${quotation.quotation_number}** del proyecto **${quotation.project?.project_number || 'N/A'}** (${quotation.project?.title || 'Sin título'}) ha sido enviada para revisión.\n\n**Enviada por:** ${userName}\n**Fecha:** ${timestamp}\n${montoDeLaTarea}\n\nPor favor revisar y aprobar o rechazar la cotización.`,
          status: 'pending',
          priority: 'high',
          category: 'quotation_review',
          created_by: user.id,
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // Assign task to all admins — y AVISARLES (#555).
      //
      // Esto asignaba y se iba. La tarea de revisión quedaba en la lista de
      // todos los admins sin que a ninguno le sonara la campana, así que la
      // cotización se quedaba en `under_review` hasta que alguien entrara a
      // mirar por su cuenta. Es el mismo agujero que #555 midió en el flujo de
      // Jorge (0 notificaciones de tarea sobre 21 asignaciones en prod), en
      // otro archivo del mismo carril web.
      //
      // El error del insert tampoco se miraba: si la RLS rechazaba las
      // asignaciones, esto devolvía `{ data: quotation, error: null }` y la UI
      // reportaba éxito sobre una tarea que no le tocó a nadie.
      if (adminIds.length > 0 && newTask) {
        const assignments = adminIds.map(adminId => ({
          task_id: newTask.id,
          user_id: adminId,
          assigned_by: user.id,
        }));

        const { data: inserted, error: assignError } = await supabase
          .from('task_assignments')
          .insert(assignments)
          .select('user_id');

        if (assignError) {
          // La cotización YA está en revisión: esto no se revierte, se dice.
          return {
            data: quotation,
            error: null,
            warning:
              'La cotización quedó en revisión, pero no se pudo asignar la tarea a los administradores. ' +
              'Avisales por otro medio.' + (avisoMargen ? ` ${avisoMargen}` : ''),
          };
        }

        // Sólo se notifica a quien la base REALMENTE dejó asignar. Notificar
        // sobre `adminIds` mandaría avisos de una tarea que el destinatario no
        // tiene — el mismo "verde sobre nada" al revés.
        const asignados = (inserted || []).map((a) => a.user_id);
        const notifyWarning = await notifyAssignees(asignados, user.id, {
          notification_type: 'task',
          title: 'Nueva tarea asignada',
          message: `Se te ha asignado la tarea: ${newTask.title}`,
          action_url: `/tareas/${newTask.id}`,
          related_type: 'task',
          related_id: newTask.id,
        });

        if (notifyWarning) {
          return {
            data: quotation,
            error: null,
            warning: notifyWarning + (avisoMargen ? ` ${avisoMargen}` : ''),
          };
        }
      }
    }

    return { data: quotation, error: null, ...(avisoMargen ? { warning: avisoMargen } : {}) };
  } catch (error) {
    console.error('Submit quotation for review error:', error);
    return { data: null, error };
  }
};

/**
 * #609 — La compuerta del precio en el carril web. Gemela de
 * `assertPricesConfirmed` en `mcp-server/src/tools/quotationTools.js`, en el
 * mismo punto donde #593 puso la del margen: no es una segunda compuerta, es la
 * misma con un campo más. El trigger `trg_quotation_margin_before_send` lo
 * bloquea igual en la base; esto es la redacción.
 *
 * @param {string} quotationId
 * @param {string} action - Qué se está intentando, para el mensaje
 * @throws {Error} Cuando alguna línea sigue sin confirmar
 */
/**
 * #656 — LA PRECONDICIÓN QUE #593 Y #609 DAN POR SENTADA Y NINGUNA VERIFICA.
 *
 * Las dos compuertas preguntan lo mismo en el fondo —¿cuántas líneas hay sin
 * confirmar?— y las dos dejan pasar si la respuesta es cero. Con una cotización
 * SIN ÍTEMS la respuesta es cero, porque cero líneas. Pasan las dos, y pasan
 * limpiamente: sin warning, sin nada raro en el log. Se aprueba y se marca como
 * enviada una cotización vacía con el sello de dos guardas que "verificaron".
 *
 * Es peor que no tener la compuerta: quien la lea después no vuelve a mirar, ya
 * la revisó el sistema. Una compuerta que cuenta problemas necesita que exista
 * el universo sobre el que cuenta.
 *
 * Gemela de `assertQuotationHasItems` en el carril MCP
 * (mcp-server/src/tools/quotationTools.js). Falla CERRADA: si la cuenta no se
 * puede leer, no se aprueba ni se envía — al revés que `assertPricesConfirmed`,
 * que puede fallar abierta porque su copia dura es el trigger.
 *
 * @param {string} quotationId
 * @param {string} action - Qué se está intentando, para el mensaje
 * @throws {Error} Cuando la cotización no tiene ni una línea
 */
const assertQuotationHasItems = async (quotationId, action) => {
  const { count, error } = await supabase
    .from('quotation_items')
    .select('id', { count: 'exact', head: true })
    .eq('quotation_id', quotationId);

  if (error) throw error;

  if (!count) {
    throw new Error(
      `No se puede ${action} una cotización sin líneas. Una cotización vacía ` +
      'no tiene nada que revisar, nada que aprobar y nada que enviar: los ' +
      'chequeos del margen y del precio la dejan pasar justamente porque no ' +
      'hay líneas que mirar. Agregá al menos un ítem (material, mano de obra o ' +
      'herramienta) y volvé a intentarlo. Si todavía no tenés los ítems, ' +
      'dejala como borrador.'
    );
  }
};

const assertPricesConfirmed = async (quotationId, action) => {
  const { data, error } = await supabase
    .from('quotation_items')
    .select('id, description, unit_price, price_source')
    .eq('quotation_id', quotationId)
    .eq('price_source', PRICE_ROW_SOURCE.UNCONFIRMED);

  // Falla ABIERTA: la copia que bloquea de verdad es el trigger. No se traba
  // una cotización legítima por un problema de lectura.
  if (error || !data || data.length === 0) return;

  throw new Error(unconfirmedPricesMessage(data, action));
};

/**
 * Approve quotation (admin only)
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<{data, error}>}
 */
export const approveQuotation = async (quotationId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // #656: la precondición, ANTES de las dos compuertas. Si corriera después,
    // una cotización vacía sin margen rebotaría por #593 y el rechazo se leería
    // como si esta guarda hubiera funcionado.
    await assertQuotationHasItems(quotationId, 'aprobar');

    // #593: la compuerta. Aprobar es el paso previo a que la cotización salga
    // hacia el cliente, y es donde el margen tiene que existir — no al crearla.
    // Gemela de `assertMarginDecided` en el carril MCP; el trigger
    // `trg_quotation_margin_before_send` lo bloquea igual en la base.
    const { data: current, error: currentError } = await supabase
      .from('quotations')
      .select('risk_percentage, profit_percentage')
      .eq('id', quotationId)
      .single();
    if (currentError) throw currentError;

    const readiness = validateQuotationReadyToSend(current);
    if (!readiness.isValid) {
      throw new Error(`No se puede aprobar la cotización. ${readiness.message}`);
    }

    // #609: la misma compuerta, el otro campo.
    await assertPricesConfirmed(quotationId, 'aprobar');

    const { data, error } = await supabase
      .from('quotations')
      .update({
        status: QUOTATION_STATUS.APPROVED,
        approved_by: user.id,
      })
      .eq('id', quotationId)
      .select()
      .single();

    if (error) throw error;

    // #639 — LA PRIMERA APROBACION FIJA EL MONTO CONTRATADO; NINGUNA POSTERIOR
    // LO TOCA.
    //
    // Esto hacía `.update({ quoted_amount: data.total })` a secas, así que
    // aprobar un adicional, una revisión o una segunda etapa BORRABA la línea
    // base del proyecto y la reemplazaba por el monto del adicional. Medido dos
    // veces el 2026-08-09 contra el agente vivo: Q168EQ-SL-0717 pasó de
    // 2.839.261 a 36.767,94 y PM-2026-001 de 2.804.208 a 143.193,60. Aguas
    // abajo `check_budget_status` reportó PM-2026-001 al 683% consumido, en
    // rojo crítico, sin que cambiara un solo gasto.
    //
    // `projects.quoted_amount` es la LINEA BASE CONTRATADA: es el denominador
    // del semáforo de presupuesto, del variance y de los KPI. La razón completa
    // de por qué no se suman las cotizaciones aprobadas ni se calcula al leer
    // está escrita una sola vez, en el gemelo del carril MCP
    // (`mcp-server/src/tools/quotationTools.js`, `setProjectBaselineIfAbsent`).
    // Los tres carriles aplican la MISMA regla; el guard va en el WHERE
    // (`.is('quoted_amount', null)`) y no en un read-then-write, así que cero
    // filas afectadas significa "ya había línea base", no un error.
    //
    // Cambiar un monto contratado sigue siendo posible — con `updateProject`,
    // a propósito. Lo que deja de existir es hacerlo sin querer al aprobar.
    if (data.project_id && data.total) {
      const { error: baselineError } = await supabase
        .from('projects')
        .update({ quoted_amount: data.total })
        .eq('id', data.project_id)
        .is('quoted_amount', null);

      if (baselineError) throw baselineError;
    }

    // Auto-complete quotation_review tasks for this project
    if (data.project_id) {
      const { data: reviewTasks } = await supabase
        .from('tasks')
        .select('id')
        .eq('project_id', data.project_id)
        .eq('category', 'quotation_review')
        .in('status', ['pending', 'in_progress']);

      if (reviewTasks && reviewTasks.length > 0) {
        const taskIds = reviewTasks.map(t => t.id);
        await supabase
          .from('tasks')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
          })
          .in('id', taskIds);
      }
    }

    // Notify the quotation creator (fire-and-forget)
    if (data.created_by && data.created_by !== user.id) {
      createNotification({
        user_id: data.created_by,
        notification_type: 'approval',
        title: 'Cotización aprobada',
        message: `La cotización ${data.quotation_number} ha sido aprobada`,
        action_url: data.project_id ? `/proyectos/${data.project_id}` : undefined,
        related_type: 'project',
        related_id: data.project_id || undefined,
      }).catch(err => console.error('Notification error:', err));
    }

    return { data, error: null };
  } catch (error) {
    console.error('Approve quotation error:', error);
    return { data: null, error };
  }
};

/**
 * Reject quotation (admin only)
 *
 * #A5: esto hacía `.update({ status:'rejected', notes: reason })` — PISABA
 * las notas comerciales de la cotización con el motivo del rechazo, sin
 * aviso y sin vuelta atrás. `quotations` no tiene columna para el motivo
 * (verificado contra prod: id, project_id, quotation_number, status,
 * subtotal, tax_rate, tax_amount, total, risk_percentage, profit_percentage,
 * notes, created_by, approved_by, approved_at, sent_at, created_at,
 * updated_at, supervisor_id, lider_id, sent_by, accepted_at), así que se
 * reutilizó `notes` — que además es el mismo campo que QuotationBuilder
 * vuelve a cargar en el textarea del presupuesto.
 *
 * Mientras no exista la columna propia, el motivo se AGREGA al final de las
 * notas en vez de reemplazarlas: nada de lo que el usuario escribió se
 * pierde, y el motivo queda visible donde ya lo iba a buscar.
 *
 * MIGRACIÓN RECOMENDADA (no aplicada acá): agregar
 * `quotations.rejection_reason text` + `rejected_at timestamptz` y mover el
 * motivo ahí, dejando `notes` como lo que dice ser. En cuanto exista, esta
 * función debe escribir en la columna nueva y dejar `notes` intacto.
 *
 * @param {string} quotationId - Quotation ID
 * @param {string} reason - Rejection reason
 * @returns {Promise<{data, error}>}
 */
export const rejectQuotation = async (quotationId, reason) => {
  try {
    const { data: current, error: readError } = await supabase
      .from('quotations')
      .select('notes')
      .eq('id', quotationId)
      .single();

    if (readError) throw readError;

    const stamp = new Date().toISOString().slice(0, 10);
    const entry = `[Rechazada ${stamp}] ${String(reason ?? '').trim()}`;
    const existing = (current?.notes || '').trim();
    const notes = existing ? `${existing}\n\n${entry}` : entry;

    const { data, error } = await supabase
      .from('quotations')
      .update({
        status: QUOTATION_STATUS.REJECTED,
        notes,
      })
      .eq('id', quotationId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Reject quotation error:', error);
    return { data: null, error };
  }
};

/**
 * Mark quotation as sent to client
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<{data, error}>}
 */
export const markQuotationSent = async (quotationId) => {
  try {
    // #656: la precondición, ANTES de las dos compuertas. Marcar como enviada
    // una cotización vacía le dice al cliente que recibió algo que no existe.
    await assertQuotationHasItems(quotationId, 'marcar como enviada');

    // #593: misma compuerta que en `approveQuotation`. Enviar sin margen es
    // exactamente lo que #339 quería impedir; lo que cambió es DÓNDE se impide.
    const { data: current, error: currentError } = await supabase
      .from('quotations')
      .select('risk_percentage, profit_percentage')
      .eq('id', quotationId)
      .single();
    if (currentError) throw currentError;

    const readiness = validateQuotationReadyToSend(current);
    if (!readiness.isValid) {
      throw new Error(`No se puede marcar la cotización como enviada. ${readiness.message}`);
    }

    // #609: la misma compuerta, el otro campo.
    await assertPricesConfirmed(quotationId, 'marcar como enviada');

    const { data, error } = await supabase
      .from('quotations')
      .update({
        status: QUOTATION_STATUS.SENT,
        sent_at: new Date().toISOString(),
      })
      .eq('id', quotationId)
      .select()
      .single();

    if (error) throw error;

    // #507: esto escribia `status: 'quoted'`, que el CHECK vigente NO admite
    // (draft, quotation_sent, quotation_accepted, po_received, in_progress,
    // closed -- ver 20260715200000). Y el error no se revisaba, asi que
    // marcar una cotizacion como enviada NUNCA movio el proyecto: fallaba en
    // silencio y la funcion devolvia { error: null }.
    //
    // El valor correcto es 'quotation_sent': es literalmente lo que acaba de
    // pasar, y es el estado que #353 dejo de asignar al CREAR el proyecto
    // justamente para que solo lo ponga quien de verdad envio la cotizacion.
    if (data.project_id) {
      const { error: projectError } = await supabase
        .from('projects')
        .update({ status: 'quotation_sent' })
        .eq('id', data.project_id);

      if (projectError) throw projectError;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Mark quotation sent error:', error);
    return { data: null, error };
  }
};

/**
 * Accept quotation (admin only)
 * Calls the accept_quotation_and_activate_project RPC for an atomic
 * acceptance workflow: updates quotation status, rejects siblings,
 * activates project, and creates an audit log entry.
 *
 * @param {string} quotationId - Quotation ID
 * @param {string|null} poNumber - Optional PO number
 * @returns {Promise<{data, error}>}
 */
export const acceptQuotation = async (quotationId, poNumber = null) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase.rpc('accept_quotation_and_activate_project', {
      p_quotation_id: quotationId,
      p_user_id: user.id,
      p_po_number: poNumber || null,
    });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Accept quotation error:', error);
    return { data: null, error };
  }
};

/**
 * Delete quotation
 * @param {string} quotationId - Quotation ID
 * @returns {Promise<{success, error}>}
 */
export const deleteQuotation = async (quotationId) => {
  try {
    const { error } = await supabase
      .from('quotations')
      .delete()
      .eq('id', quotationId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete quotation error:', error);
    return { success: false, error };
  }
};

/**
 * Reassign the lider (technical leader) on a quotation.
 *
 * Issue #129: RACI flow — the supervisor responsible for a quotation can
 * delegate (or re-delegate) the lider role to any technician. RLS policy
 * `lider_update_assigned_quotations` allows the supervisor (or admin)
 * to perform this UPDATE on `quotations.lider_id`.
 *
 * @param {string} quotationId - Quotation UUID
 * @param {string} liderId - Profile UUID of the new lider (role=technician)
 * @returns {Promise<{data, error}>}
 */
export const assignLiderToQuotation = async (quotationId, liderId) => {
  try {
    if (!quotationId) throw new Error('quotationId is required');
    if (!liderId) throw new Error('liderId is required');

    const { data, error } = await supabase
      .from('quotations')
      .update({ lider_id: liderId })
      .eq('id', quotationId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Assign lider to quotation error:', error);
    return { data: null, error };
  }
};

const quotationService = {
  listQuotations,
  listMyQuotations,
  getQuotation,
  createQuotation,
  updateQuotation,
  assignLiderToQuotation,
  addQuotationItem,
  updateQuotationItem,
  deleteQuotationItem,
  recalculateQuotationTotals,
  submitQuotationForReview,
  approveQuotation,
  rejectQuotation,
  markQuotationSent,
  acceptQuotation,
  deleteQuotation,
};

export default quotationService;
